import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../ui/cn';
import { Button } from '../ui/button';
import type { DoorViewSide } from '../surfaces/doorViewSide';
import type { DoorConfiguration } from '../types/doorConfiguration';
import { DoorSvgRenderer } from '../renderer/DoorSvgRenderer';
import {
  PREVIEW_FALLBACK_VIEWPORT_ASPECT,
  PREVIEW_PAN_THRESHOLD_PX,
  clientToSvgUser,
  clampPreviewScale,
  screenDeltaToUser,
  wheelScaleFactor,
  worldCentre,
} from './previewZoom';
import { usePreviewZoom } from './usePreviewZoom';

export const LiveDoorPreview: React.FC<{
  configuration: DoorConfiguration;
  viewSide?: DoorViewSide;
  compact?: boolean;
  debugHardwareGuides?: boolean;
}> = ({ configuration, viewSide = 'external', compact = false, debugHardwareGuides = false }) => {
  const world = useMemo(
    () => ({
      x: 0,
      y: 0,
      width: configuration.dimensions.overallWidthMm,
      height: configuration.dimensions.overallHeightMm,
    }),
    [configuration.dimensions.overallHeightMm, configuration.dimensions.overallWidthMm]
  );
  const interactive = !compact;
  const canvasRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportAspect, setViewportAspect] = useState(PREVIEW_FALLBACK_VIEWPORT_ASPECT);
  const { viewBox, zoomed, scale, reset, zoomToward, toggleAt, panBy } = usePreviewZoom(
    world,
    interactive,
    viewportAspect
  );
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const [panning, setPanning] = useState(false);
  const pointerRef = useRef<{
    id: number;
    x: number;
    y: number;
    dragged: boolean;
  } | null>(null);
  const skipClickRef = useRef(false);

  useEffect(() => {
    const node = canvasRef.current;
    if (!interactive || !node || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      if (node.clientWidth > 0 && node.clientHeight > 0) {
        setViewportAspect(node.clientWidth / node.clientHeight);
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [interactive]);

  useEffect(() => {
    if (!interactive || !zoomed) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target as Element | null;
      if (target?.closest?.('[role="dialog"]')) return;
      event.preventDefault();
      reset();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [interactive, reset, zoomed]);

  const previewSvg = () =>
    viewportRef.current?.querySelector<SVGSVGElement>('[data-part="door-preview"]') ?? null;

  const eventOnDoor = (target: EventTarget | null) => {
    const svg = previewSvg();
    return Boolean(svg && target instanceof Node && svg.contains(target));
  };

  useEffect(() => {
    const node = viewportRef.current;
    if (!interactive || !node) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (!eventOnDoor(event.target)) return;
      event.preventDefault();
      const svg = previewSvg();
      if (!svg) return;
      const factor = wheelScaleFactor(event.deltaY, event.deltaMode);
      const nextScale = clampPreviewScale(scaleRef.current * factor);
      zoomToward(nextScale, clientToSvgUser(svg, event.clientX, event.clientY));
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [interactive, zoomToward]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive || event.button > 0 || !eventOnDoor(event.target)) return;
    pointerRef.current = { id: event.pointerId, x: event.clientX, y: event.clientY, dragged: false };
    viewportRef.current?.focus();
    if (zoomed && typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const pointer = pointerRef.current;
    if (!pointer || pointer.id !== event.pointerId) return;
    const dx = event.clientX - pointer.x;
    const dy = event.clientY - pointer.y;
    if (!pointer.dragged && Math.hypot(dx, dy) > PREVIEW_PAN_THRESHOLD_PX) {
      pointer.dragged = true;
    }
    if (!zoomed || !pointer.dragged) return;
    const svg = previewSvg();
    if (!svg) return;
    event.preventDefault();
    setPanning(true);
    const delta = screenDeltaToUser(svg, dx, dy);
    panBy(delta.x, delta.y);
    pointer.x = event.clientX;
    pointer.y = event.clientY;
  };

  const endPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    const pointer = pointerRef.current;
    if (!pointer || pointer.id !== event.pointerId) return;
    if (pointer.dragged) skipClickRef.current = true;
    pointerRef.current = null;
    setPanning(false);
    if (
      typeof event.currentTarget.hasPointerCapture === 'function' &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    if ((event.target as Element | null)?.closest?.('button')) return;
    if (!eventOnDoor(event.target)) return;
    const svg = previewSvg();
    if (!svg) return;
    toggleAt(clientToSvgUser(svg, event.clientX, event.clientY));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleAt(worldCentre(world));
      return;
    }
    if (event.key === 'Escape' && zoomed) {
      event.preventDefault();
      reset();
    }
  };

  return (
    <div
      ref={canvasRef}
      className={cn(
        'door-designer-preview relative flex w-full items-center justify-center overflow-hidden',
        compact ? 'h-32 min-h-32' : 'h-full min-h-[220px] md:min-h-0'
      )}
      data-door-designer-preview={compact ? undefined : true}
      data-hardware-debug={compact ? undefined : debugHardwareGuides ? 'true' : 'false'}
      data-preview-zoom={compact ? undefined : zoomed ? 'zoomed' : 'fit'}
      data-preview-panning={panning || undefined}
      aria-hidden={compact || undefined}
    >
      <div
        ref={viewportRef}
        data-preview-zoom-viewport={interactive || undefined}
        className={cn(
          'flex h-full min-h-0 w-full items-center justify-center overflow-hidden',
          interactive && 'outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
        )}
        style={{ touchAction: zoomed ? 'none' : interactive ? 'manipulation' : undefined }}
        tabIndex={interactive ? 0 : undefined}
        role={interactive ? 'region' : undefined}
        aria-label={
          interactive
            ? zoomed
              ? 'Interactive door preview, zoomed in. Click the door or press Enter to reset. Drag to pan. Press Escape to reset.'
              : 'Interactive door preview. Click the door to zoom in, or press Enter to zoom.'
            : undefined
        }
        onPointerDown={interactive ? onPointerDown : undefined}
        onPointerMove={interactive ? onPointerMove : undefined}
        onPointerUp={interactive ? endPointer : undefined}
        onPointerCancel={interactive ? endPointer : undefined}
        onClick={interactive ? onClick : undefined}
        onKeyDown={interactive ? onKeyDown : undefined}
      >
        <DoorSvgRenderer
          configuration={configuration}
          viewSide={viewSide}
          viewBox={interactive ? viewBox : undefined}
          debugHardwareGuides={debugHardwareGuides}
        />
      </div>
      {interactive && zoomed ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="absolute right-2 top-2 z-10 shadow-sm"
          aria-label="Reset view"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            reset();
          }}
        >
          Reset view
        </Button>
      ) : null}
    </div>
  );
};
